import { Ico } from "@/assets/icons";
import { colors } from "@/components/Theme/colors";
import Button from "@/shared/components/atoms/Button/Button";
import { SelectTagTooltip } from "@/shared/components/atoms/SelectTagTooltip/SelectTagTooltip";
import { Box, Divider, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { formatPrice } from "@/shared/utils/formatPrice";
import {
  isEcuadorCountry,
  useIsEcuadorMarket,
} from "@/features/products/utils/userCurrency";
import { useTranslation } from "react-i18next";

// --- Component Definition ---

interface PurchaseProgramCardProps {
  isLoading?: boolean;
  title: string;
  originalPrice?: number;
  currentPrice: number;
  currency: string;
  /**
   * The market the mentor authored the base price for, when the currency does
   * not name it: Ecuador prices in USD, the same code as the United States.
   * Wins over the buyer's own market, because it is what the price row is.
   */
  baseCountry?: string | null;
  discountPercentage?: number;
  features: string[];
  includeStartCTA?: boolean;
  //onPrimaryAction: () => void;
  onSecondaryAction: () => void;
  onLinkNow: () => void;
  isLinkLoading?: boolean;
  primaryCtaLabel?: string;
  secondaryCtaLabel?: string;
  footerText?: string;
  footerIcon?: React.ReactNode | null;
  isPrimaryDisabled?: boolean;
  showPrice?: boolean;
  isPriceLoading?: boolean;
  showSelectTag?: boolean;
  selectDescription?: string;
}

/**
 * PurchaseProgramCard is an organism component that displays product information,
 * pricing, features, and purchase options in a styled card format.
 * It is designed to be reusable and driven by external data, fitting into an
 * atomic design methodology.
 */
export const PurchaseProgramCard: React.FC<PurchaseProgramCardProps> = ({
  isLoading = false,
  title,
  originalPrice,
  currentPrice,
  currency,
  baseCountry = null,
  discountPercentage,
  features,
  includeStartCTA = false,
  //onPrimaryAction,
  onSecondaryAction,
  onLinkNow,
  isLinkLoading = false,
  primaryCtaLabel,
  secondaryCtaLabel,
  footerText,
  footerIcon,
  isPrimaryDisabled = false,
  showPrice = true,
  isPriceLoading = false,
  showSelectTag = false,
  selectDescription = "Los productos Select son programas de edición exclusiva, disponibles únicamente como compra independiente. No están incluidos en planes, ni suscripciones de Beemo.",
}) => {
  const { t } = useTranslation();
  const countryFlagSize = 32;
  const { isEcuador: isEcuadorMarket, isResolved: isMarketResolved } =
    useIsEcuadorMarket();

  const primaryButtonLabel = primaryCtaLabel ?? "Cómpralo ahora";
  const secondaryButtonLabel = secondaryCtaLabel ?? "Pruébalo gratis";
  const cardFooterText = footerText ?? "OFERTA POR TIEMPO LIMITADO";
  const cardFooterIcon =
    footerIcon === null ? null : footerIcon ?? <Ico.StopWatch />;
  const selectToken = "SELECT";
  const selectIndex = selectDescription.indexOf(selectToken);
  const hasSelectToken = selectIndex !== -1;
  const selectDescriptionPrefix = hasSelectToken
    ? selectDescription.slice(0, selectIndex)
    : selectDescription;
  const selectDescriptionSuffix = hasSelectToken
    ? selectDescription.slice(selectIndex + selectToken.length)
    : "";

  const getCountryFlagByCurrency = () => {
    switch (currency) {
      case "USD":
        // Ecuador charges USD too, so the code alone cannot name the market.
        // What the mentor authored wins — that price row IS the Ecuador one,
        // whoever is looking at it.
        // Arrives as the platform's country identifier ("ECUADOR"), not an ISO
        // code, so it is matched against the name rather than the alpha-3.
        if (isEcuadorCountry(null, baseCountry)) {
          return (
            <Ico.Ecuador height={countryFlagSize} width={countryFlagSize} />
          );
        }
        // Nothing authored, so fall back to the buyer's own market. Until an
        // anonymous visitor's is known the slot stays empty but reserved:
        // painting the US flag first and swapping it a moment later reads as a
        // glitch, and dropping it entirely shifts the price sideways.
        if (!isMarketResolved) {
          return <Box w={`${countryFlagSize}px`} h={`${countryFlagSize}px`} />;
        }
        return isEcuadorMarket ? (
          <Ico.Ecuador height={countryFlagSize} width={countryFlagSize} />
        ) : (
          <Ico.Usa height={countryFlagSize} width={countryFlagSize} />
        );
      case "MXN":
        return <Ico.Mexico height={countryFlagSize} width={countryFlagSize} />;
      case "COP":
        return (
          <Ico.Colombia height={countryFlagSize} width={countryFlagSize} />
        );
      case "PEN":
        return <Ico.Peru height={countryFlagSize} width={countryFlagSize} />;
      case "CLP":
        return <Ico.Chile height={countryFlagSize} width={countryFlagSize} />;
      case "ARS":
        return (
          <Ico.Argentina height={countryFlagSize} width={countryFlagSize} />
        );
      case "BRL":
        return <Ico.Brazil height={countryFlagSize} width={countryFlagSize} />;
      case "UYU":
        return <Ico.Uruguay height={countryFlagSize} width={countryFlagSize} />;
      default:
        return <Ico.Usa height={countryFlagSize} width={countryFlagSize} />;
    }
  };

  // Display a spinner while data is being loaded, matching the old component's pattern.
  if (isLoading) {
    return (
      <Flex
        border="solid 2px"
        borderColor={colors.primary[300]}
        borderRadius="6px"
        p={4}
        w="100%"
        justifyContent="center"
        alignItems="center"
        minH="365px" // Provide a minimum height for visual consistency during load.
        data-testid="purchase-program-card-loading"
      >
        <Spinner color={colors.primary[300]} />
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      border="solid 1px"
      borderColor={colors.neutral[100]}
      borderRadius="6px"
      p={4}
      w="100%"
      bg="white"
      data-testid="purchase-program-card"
    >
      {showSelectTag && (
        <>
          <Box>
            <SelectTagTooltip />
            <Text
              mt={"8px"}
              fontFamily="Inter"
              fontSize="12px"
              fontWeight={500}
              lineHeight="19.2px"
              color={colors.secondary[300]}
            >
              {hasSelectToken ? (
                <>
                  {selectDescriptionPrefix}
                  <Text
                    as="span"
                    fontWeight={700}
                    fontSize="12px"
                    lineHeight="19.2px"
                  >
                    {selectToken}
                  </Text>
                  {selectDescriptionSuffix}
                </>
              ) : (
                selectDescription
              )}
            </Text>
          </Box>
          <Divider borderColor={colors.neutral[100]} my={"16px"} />
        </>
      )}
      {/* --- Header Title --- */}
      <Text
        fontFamily="Poppins"
        fontWeight="600"
        fontSize="20px"
        marginBottom={"8px"}
      >
        {title}
      </Text>

      {/* --- Price Section --- */}
      {showPrice && (
        <VStack align="flex-start" spacing={0}>
          {isPriceLoading ? (
            <Spinner color={colors.primary[300]} />
          ) : (
            <>
              {originalPrice && (
                <Text
                  fontFamily="Poppins"
                  fontSize="20px"
                  color={"#F00004"}
                  fontWeight={600}
                  textDecoration="line-through"
                  data-testid="purchase-program-original-price"
                >
                  {formatPrice(currency, originalPrice)}
                </Text>
              )}
              <Flex align="center" gap={2} wrap="wrap">
                {getCountryFlagByCurrency()}
                <Text
                  fontFamily="Poppins"
                  fontWeight={600}
                  fontSize="32px"
                  data-testid="purchase-program-price"
                >
                  {formatPrice(currency, currentPrice)}
                </Text>
                <Text
                  fontFamily="Inter"
                  fontWeight="500"
                  fontSize="14px"
                  as="em"
                  color={colors.secondary[300]}
                  marginTop={2}
                >
                  {t("single payment")}
                </Text>
                {discountPercentage && (
                  <Box
                    bg={"#F000041A"}
                    color={"#F00004"}
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    <Text fontFamily="Inter" fontWeight="bold" fontSize="12px">
                      Ahorra {discountPercentage}%
                    </Text>
                  </Box>
                )}
              </Flex>
            </>
          )}
        </VStack>
      )}

      {/* --- Features List --- */}
      {features.length > 0 && (
        <VStack
          align="flex-start"
          spacing={1.5}
          marginTop={"4px"}
          marginBottom={"16px"}
        >
          {features.map((feature, index) => (
            <Flex key={index} gap={2} align="center">
              <Box
                as={Ico.Check}
                width="20px"
                height="20px"
                minWidth="20px"
                minHeight="20px"
                flexShrink={0}
                color={colors.success[300]}
              />
              <Text
                fontFamily="Inter"
                fontWeight={500}
                fontSize="12px"
                color={colors.secondary[100]}
              >
                {feature}
              </Text>
            </Flex>
          ))}
        </VStack>
      )}

      {/* --- Call-to-Action Buttons --- */}
      <VStack w="100%" spacing={3}>
        <Button
          height={"48px"}
          width={"100%"}
          variant="primary"
          onClick={onLinkNow}
          isLoading={isLinkLoading}
          isDisabled={isLinkLoading || isPrimaryDisabled}
          data-testid="purchase-program-primary-cta"
        >
          {primaryButtonLabel}
        </Button>
        {includeStartCTA && (
          <Button
            height={"48px"}
            width={"100%"}
            variant="secondary"
            onClick={onSecondaryAction}
            data-testid="purchase-program-secondary-cta"
          >
            {secondaryButtonLabel}
          </Button>
        )}
      </VStack>

      {/* --- Limited Offer Footer --- */}
      <Flex justifyContent="center" alignItems="center" gap={2} pt={"12px"}>
        {cardFooterIcon}
        <Text
          fontSize="12px"
          fontWeight="bold"
          letterSpacing="1px" // Wider spacing for emphasis
          color={"#F00004"}
        >
          {cardFooterText}
        </Text>
      </Flex>
    </Flex>
  );
};
